"use client";

import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from "chart.js";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const getStatusColor = (status) => {
  const colors = {
    pending: "bg-yellow-100 text-yellow-700",
    selesai: "bg-green-100 text-green-700",
    batal: "bg-red-100 text-red-700",
  };
  return colors[status?.toLowerCase()] || "bg-slate-100 text-slate-700";
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function Report({ api }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterType, setFilterType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get("/transaction");
      setData(res.data.recent_transactions || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  let filteredData =
    filterType === "all"
      ? data
      : data.filter((t) =>
          t.type?.toLowerCase().includes(filterType.toLowerCase())
        );

  if (startDate)
    filteredData = filteredData.filter((t) => new Date(t.date) >= new Date(startDate));

  if (endDate)
    filteredData = filteredData.filter((t) => new Date(t.date) <= new Date(endDate));

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const chartData = {
    labels: filteredData.map((t) => formatDate(t.date)),
    datasets: [
      {
        label: "Jumlah Transaksi",
        data: filteredData.map((t) => Number(t.amount)),
        borderWidth: 1,
      },
    ],
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transaksi");
    XLSX.writeFile(wb, "transaksi.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Laporan Transaksi", 14, 10);

    const tableData = filteredData.map((t) => [
      formatDate(t.date),
      t.description,
      t.type,
      t.amount_formatted,
      t.status,
    ]);

    autoTable(doc, {
      head: [["Tanggal", "Deskripsi", "Tipe", "Jumlah", "Status"]],
      body: tableData,
      startY: 15,
    });

    doc.save("transaksi.pdf");
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-800">Daftar Transaksi</h1>

        <button
          onClick={fetchData}
          className="px-3 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setCurrentPage(1);
          }}
          className="border rounded-xl px-3 py-2 text-sm text-slate-700"
        >
            <option value="all">Semua</option>
            <option value="pokok">Simpanan Pokok</option>
            <option value="wajib">Simpanan Wajib</option>
            <option value="sukarela">Simpanan Sukarela</option>
            <option value="angsuran">Angsuran Pinjaman</option>
        </select>

        <input
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setCurrentPage(1);
          }}
          className="border rounded-xl px-3 py-2 text-sm"
        />

        <span>-</span>

        <input
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setCurrentPage(1);
          }}
          className="border rounded-xl px-3 py-2 text-sm"
        />
      </div>

      <div className="border rounded-2xl p-4 bg-white">
        <h2 className="text-sm font-semibold text-slate-700 mb-2">
          Grafik Transaksi
        </h2>
        {filteredData.length === 0 ? (
          <div className="text-center text-slate-400 py-6">Tidak ada data</div>
        ) : (
          <Bar data={chartData} />
        )}
      </div>

      <div className="flex gap-3">
        <button
          onClick={exportExcel}
          className="px-3 py-2 rounded-xl bg-green-600 text-white text-sm hover:bg-green-700"
        >
          Export Excel
        </button>
        <button
          onClick={exportPDF}
          className="px-3 py-2 rounded-xl bg-red-600 text-white text-sm hover:bg-red-700"
        >
          Export PDF
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-4 py-3">Tanggal</th>
              <th className="text-left px-4 py-3">Deskripsi</th>
              <th className="text-left px-4 py-3">Tipe</th>
              <th className="text-left px-4 py-3">Jumlah</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-6 text-slate-400">
                  Memuat data...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-6 text-slate-400">
                  Tidak ada data transaksi
                </td>
              </tr>
            ) : (
              paginatedData.map((t, i) => (
                <tr key={i} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-700">
                    {formatDate(t.date)}
                  </td>
                  <td className="px-4 py-3">{t.description}</td>
                  <td className="px-4 py-3 text-slate-500">{t.type}</td>
                  <td className="px-4 py-3 font-semibold">
                    <span
                      className={
                        t.amount_formatted?.startsWith("+")
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {t.amount_formatted}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-lg text-xs ${getStatusColor(
                        t.status
                      )}`}
                    >
                      {t.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && filteredData.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 text-sm border-t bg-slate-50">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50"
            >
              Previous
            </button>

            <span className="text-slate-600">
              Page <b>{currentPage}</b> dari <b>{totalPages}</b>
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((p) => Math.min(totalPages, p + 1))
              }
              className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
